import React from "react";
import { Spinner } from "../../atoms/Spinner";
import { ChevronDown, ChevronRight, Filter, Plus, X, ArrowDown, Users } from "lucide-react";
import type { ComponentType } from "react";
import { storyTrailActionIcons } from "../../registry/storyTrailActionIcons";
import type { StoryTrailActionIconProps } from "../../registry/storyTrailActionIcons";
import type { StoryTrailAction } from "../../../services/storyCanvas/storyFilterChips.types";
import {
  AddFilterTrigger,
  BackButton,
  Bar,
  EmptyPill,
  FilterChip,
  FilterChipAction,
  FilterChipButton,
  FilterChipColumn,
  FilterChipRemove,
  FilterChipDrillGlyph,
  FilterChipLeadingIcon,
  FilterChipSaving,
  FilterChipTag,
  FilterChipCaret,
  FilterIndicator,
  Group,
  GroupClearButton,
  GroupLabel,
  GroupList,
  Label,
  LeadingGroup,
  ClearAllLink,
  PathCrumb,
  PathCurrent,
  PathSeparator,
  Section,
  SectionBar,
  SectionCaption,
  SectionLabel,
} from "./StoryFilterBar.styles";
import { StoryFilterChipParts } from "./StoryFilterChipParts";
import type { StoryFilterBarGroup, StoryFilterBarProps, StoryFilterBarTone } from "./StoryFilterBar.types";

/** Figma draws the trigger's plus glyph at 12×12; the chip's remove glyph matches it. */
const PLUS_SIZE = 12;
/** Funnel glyph that replaces the "Filters:" text label once a filter is applied. */
const FILTER_ICON_SIZE = 16;

/** The icon before each sectioned bar label (the design's funnel, move-arrows and down-arrow glyphs). */
const TONE_ICONS: Record<StoryFilterBarTone, ComponentType<StoryTrailActionIconProps>> = {
  global: storyTrailActionIcons.filter,
  cross: storyTrailActionIcons.cross,
  drill: ArrowDown,
};

/** Sectioned bar only: the trail action a chip kind stands for, so its chip leads with that action's icon. */
const SECTION_CHIP_ACTIONS: Partial<Record<NonNullable<StoryFilterBarGroup["chips"][number]["kind"]>, StoryTrailAction>> = {
  cross: "cross",
  include: "keep",
  exclude: "remove",
  drillDown: "drill",
};
const SECTION_ICON_SIZE = 12;
const SAVING_SPINNER_SIZE = 12;
/** Tooltip on a chip part that can't be edited, when the parent gives none. */
const DEFAULT_LOCKED_PART_TITLE = "This part can't be edited";

/**
 * Story-canvas filter header — empty (Figma 6583:205671) and applied-filter
 * (Figma 6588:209796, repeated per applied filter) states.
 *
 * Pure presentation: the parent owns whether the column dropdown is open and
 * what happens on add, on reopening a chip's panel, or on removing a chip.
 */
export const StoryFilterBar: React.FC<StoryFilterBarProps> = ({
  label = "Filters:",
  emptyLabel = "No filters applied",
  addLabel = "Add filter",
  onAddFilter,
  addFilterRef,
  isAddFilterOpen = false,
  canEditFilters = true,
  disabled = false,
  className,
  appliedFilters = [],
  onFilterChipClick,
  onFilterChipRemove,
  onFilterChipAction,
  getFilterChipRef,
  onFilterPartClick,
  getFilterPartRef,
  openPart = null,
  lockedPartTitle = DEFAULT_LOCKED_PART_TITLE,
  openFilterId = null,
  variant = "bar",
  showChipIcons = false,
  statusLabel,
  groups,
  sectioned = false,
  onClearAll,
  clearAllLabel = "Clear all",
  isPinned = false,
}) => {
  // "inline" embeds just the chip row inside another surface (a chart's
  // header) whose own icon is the add affordance — nothing renders once
  // every chip has been removed, rather than falling back to an empty pill,
  // unless the host has a status to show there.
  if (variant === "inline" && appliedFilters.length === 0 && !statusLabel) return null;

  // The chart-header ("inline") chip repeats a leading glyph per chip, since
  // there is no shared leading indicator there the way the story-level bar
  // has; the story-level chip stays glyph-less to avoid doubling up with
  // that bar's own `FilterIndicator`. Filters (global / Include / Exclude)
  // share the funnel with no +/− marker (Include/Exclude chips only appear in
  // the bar); a drill-down scope shows the drill arrow instead.
  const renderFilterChip = (
    filter: (typeof appliedFilters)[number],
    showLeadingIcon: boolean,
    tone?: StoryFilterBarTone,
  ) => {
    const sectionAction = tone && filter.kind ? SECTION_CHIP_ACTIONS[filter.kind] : undefined;
    const SectionChipIcon = sectionAction ? storyTrailActionIcons[sectionAction] : undefined;
    return (
    <FilterChip
      key={filter.id}
      $tone={tone}
      $isOpen={openFilterId === filter.id}
      $isSaving={filter.isSaving}
      data-testid="story-filter-chip"
      aria-busy={filter.isSaving || undefined}
    >
      {filter.conditions?.length ? (
        <>
          {showLeadingIcon && (
            <FilterChipLeadingIcon aria-hidden>
              {SectionChipIcon ? (
                <SectionChipIcon size={PLUS_SIZE} />
              ) : filter.kind === "drillDown" ? (
                <FilterChipDrillGlyph data-testid="story-filter-chip-drill-icon" />
              ) : (
                <Filter size={PLUS_SIZE} />
              )}
            </FilterChipLeadingIcon>
          )}
          <StoryFilterChipParts
            filter={filter}
            tone={tone}
            canEdit={canEditFilters}
            lockedTitle={lockedPartTitle}
            openPart={openPart?.filterId === filter.id ? openPart.part : null}
            onPartClick={(part, conditionIndex) =>
              (onFilterPartClick ?? ((id) => onFilterChipClick?.(id)))(filter.id, part, conditionIndex)
            }
            getPartRef={(part) => (part === "value" ? getFilterChipRef?.(filter.id) : getFilterPartRef?.(filter.id, part))}
          />
          {filter.isSaving && (
            <FilterChipSaving role="status" aria-label="Saving filter" data-testid="story-filter-chip-saving">
              <Spinner size={SAVING_SPINNER_SIZE} strokeWidth={3} />
            </FilterChipSaving>
          )}
        </>
      ) : (
        <FilterChipButton
          type="button"
          $tone={tone}
          ref={getFilterChipRef?.(filter.id)}
          // TODO: wire the new Notion-style dropdown here on click —
          // the operator/value modal this used to open was removed;
          // onFilterChipClick still fires but nothing renders yet.
          onClick={() => canEditFilters && onFilterChipClick?.(filter.id)}
          aria-haspopup="listbox"
          aria-expanded={openFilterId === filter.id}
          title={filter.title}
        >
          {showLeadingIcon && (
            <FilterChipLeadingIcon aria-hidden>
              {SectionChipIcon ? (
                <SectionChipIcon size={PLUS_SIZE} />
              ) : filter.kind === "drillDown" ? (
                <FilterChipDrillGlyph data-testid="story-filter-chip-drill-icon" />
              ) : (
                <Filter size={PLUS_SIZE} />
              )}
            </FilterChipLeadingIcon>
          )}
          {filter.columnLabel ? (
            <>
              <FilterChipColumn>{filter.columnLabel}</FilterChipColumn> {filter.label}
            </>
          ) : (
            filter.label
          )}
          {/* F22: applied chips end with a small down chevron (they open the value menu). */}
          <FilterChipCaret aria-hidden data-testid="story-filter-chip-caret">
            <ChevronDown size={PLUS_SIZE} />
          </FilterChipCaret>
          {filter.isSaving && (
            <FilterChipSaving role="status" aria-label="Saving filter" data-testid="story-filter-chip-saving">
              <Spinner size={SAVING_SPINNER_SIZE} strokeWidth={3} />
            </FilterChipSaving>
          )}
        </FilterChipButton>
      )}
      {filter.tag && <FilterChipTag>{filter.tag}</FilterChipTag>}
      {canEditFilters && filter.actionLabel && onFilterChipAction && (
        <FilterChipAction
          type="button"
          aria-label={filter.actionLabel}
          title={filter.actionLabel}
          disabled={filter.isSaving}
          onClick={() => onFilterChipAction(filter.id)}
        >
          <Users size={PLUS_SIZE} aria-hidden />
        </FilterChipAction>
      )}
      {canEditFilters && filter.isRemovable !== false && (
        <FilterChipRemove
          type="button"
          aria-label={`Remove ${filter.label} filter`}
          onClick={() => onFilterChipRemove?.(filter.id)}
        >
          <X size={PLUS_SIZE} aria-hidden />
        </FilterChipRemove>
      )}
    </FilterChip>
    );
  };

  const renderSection = (group: StoryFilterBarGroup) => {
    const tone = group.tone ?? "global";
    const Icon = TONE_ICONS[tone];
    const path = group.path ?? [];
    return (
      <Section key={group.id} role="group" aria-label={group.label} data-testid="story-filter-section" data-tone={tone}>
        <SectionLabel $tone={tone}>
          <Icon size={SECTION_ICON_SIZE} aria-hidden />
          {group.label}
        </SectionLabel>
        {group.chips.map((filter) =>
          renderFilterChip(filter, Boolean(filter.kind && SECTION_CHIP_ACTIONS[filter.kind]), tone),
        )}
        {path.map((step, index) => {
          const isLast = index === path.length - 1;
          return (
            <React.Fragment key={step.id ?? step.label}>
              {isLast ? (
                <PathCurrent title={step.title} aria-current="step" data-testid="story-filter-path-current">
                  {step.label}
                </PathCurrent>
              ) : (
                <>
                  {canEditFilters && group.onPathStepClick ? (
                    <PathCrumb type="button" title={step.title} onClick={() => group.onPathStepClick?.(step)}>
                      {step.label}
                    </PathCrumb>
                  ) : (
                    <SectionCaption title={step.title}>{step.label}</SectionCaption>
                  )}
                  <PathSeparator aria-hidden>
                    <ChevronRight size={PLUS_SIZE} />
                  </PathSeparator>
                </>
              )}
            </React.Fragment>
          );
        })}
        {canEditFilters && group.onBack && path.length > 0 && (
          <BackButton type="button" onClick={group.onBack}>
            {group.backLabel ?? "Back"}
          </BackButton>
        )}
        {group.caption && group.chips.length > 0 && <SectionCaption>{group.caption}</SectionCaption>}
        {canEditFilters && group.onAdd && (
          <AddFilterTrigger
            $section
            type="button"
            ref={group.addRef}
            onClick={group.onAdd}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={group.isAddOpen ?? false}
            $isOpen={group.isAddOpen}
          >
            <span aria-hidden>+</span>
            {group.addLabel ?? addLabel}
          </AddFilterTrigger>
        )}
      </Section>
    );
  };

  if (variant === "inline") {
    return (
      <LeadingGroup
        className={className}
        data-testid="story-filter-bar"
        aria-label={label}
      >
        {statusLabel && (
          <EmptyPill data-testid="story-filter-bar-status">{statusLabel}</EmptyPill>
        )}
        {appliedFilters.map((filter) => renderFilterChip(filter, true))}
      </LeadingGroup>
    );
  }

  if (groups && sectioned) {
    // A section shows while it has chips, a path or, for an editor, a way to add one; Global is
    // the one that always has its add trigger, so the bar is never empty for an editor.
    const shown = groups.filter(
      (group) => group.chips.length > 0 || (group.path?.length ?? 0) > 0 || (canEditFilters && group.onAdd),
    );
    return (
      <SectionBar $pinned={isPinned} className={className} role="region" data-testid="story-filter-bar" aria-label={label}>
        {shown.length === 0 ? (
          <Section>
            <Label>{label}</Label>
            <EmptyPill data-testid="story-filter-bar-empty">{emptyLabel}</EmptyPill>
          </Section>
        ) : (
          shown.map((group) => renderSection(group))
        )}
        {canEditFilters && onClearAll && (
          <ClearAllLink type="button" onClick={onClearAll}>
            {clearAllLabel}
          </ClearAllLink>
        )}
      </SectionBar>
    );
  }

  if (groups) {
    // A group shows while it has chips or, for an editor, a way to add one.
    const shown = groups.filter((group) => group.chips.length > 0 || (canEditFilters && group.onAdd));
    return (
      <Bar className={className} data-testid="story-filter-bar" aria-label={label}>
        {shown.length === 0 ? (
          <LeadingGroup>
            <Label>{label}</Label>
            <EmptyPill data-testid="story-filter-bar-empty">{emptyLabel}</EmptyPill>
          </LeadingGroup>
        ) : (
          <GroupList>
            {shown.map((group) => (
              <Group key={group.id} role="group" aria-label={group.label} data-testid="story-filter-group">
                <GroupLabel>{group.label}</GroupLabel>
                {group.chips.map((filter) => renderFilterChip(filter, showChipIcons))}
                {canEditFilters && group.onAdd && (
                  <AddFilterTrigger
                    type="button"
                    ref={group.addRef}
                    onClick={group.onAdd}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={group.isAddOpen ?? false}
                    $isOpen={group.isAddOpen}
                  >
                    <Plus size={PLUS_SIZE} aria-hidden />
                    {group.addLabel ?? addLabel}
                  </AddFilterTrigger>
                )}
                {canEditFilters && group.onClear && group.chips.length > 0 && (
                  <GroupClearButton type="button" onClick={group.onClear}>
                    {group.clearLabel ?? "Clear"}
                  </GroupClearButton>
                )}
              </Group>
            ))}
          </GroupList>
        )}
      </Bar>
    );
  }

  const chipList = appliedFilters.map((filter) => renderFilterChip(filter, showChipIcons));

  return (
    <Bar className={className} data-testid="story-filter-bar" aria-label={label}>
      <LeadingGroup>
        {appliedFilters.length === 0 ? (
          <Label>{label}</Label>
        ) : (
          <FilterIndicator aria-hidden>
            <Filter size={FILTER_ICON_SIZE} />
          </FilterIndicator>
        )}
        {appliedFilters.length === 0 ? (
          <EmptyPill data-testid="story-filter-bar-empty">{emptyLabel}</EmptyPill>
        ) : (
          chipList
        )}

        {canEditFilters && (
          <AddFilterTrigger
            type="button"
            ref={addFilterRef}
            onClick={onAddFilter}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isAddFilterOpen}
            $isOpen={isAddFilterOpen}
          >
            <Plus size={PLUS_SIZE} aria-hidden />
            {addLabel}
          </AddFilterTrigger>
        )}
      </LeadingGroup>
    </Bar>
  );
};

export default StoryFilterBar;
